// Component: ManualOrdersPanel.jsx
// Purpose: A WebSockets-primary panel for manual orders and positions, with per-order expectation timers for REST reconciliation.
import React, { useState, useEffect, useRef } from 'react';
import { useWebSocket } from '../../hooks/useWebSocket';
import api from '../../services/api';
import { mapEventTypeToStatus } from '../../utils/tradingTransforms';
//* Shared reject-reason map — also used by SmartOrderForm notification path.
import { rejectReasonLabel } from '../../utils/rejectReason';

const SUBMITTED_GRACE_MS = Number(process.env.REACT_APP_MANUAL_SUBMITTED_GRACE_MS) || 2000;
const TERMINAL_GRACE_MS = Number(process.env.REACT_APP_MANUAL_TERMINAL_GRACE_MS) || 5000;

const TERMINAL_STATUSES = new Set(['FILLED', 'CANCELLED', 'REJECTED']);

const ManualOrdersPanel = ({ userId, armedOrders = [], refreshSignal = 0 }) => {
  const [ordersMap, setOrdersMap] = useState({});
  const [positionsMap, setPositionsMap] = useState({});
  const ordersRef = useRef(new Map());
  const versionRef = useRef(0);
  
  // Force render helper
  const forceRender = () => {
    versionRef.current += 1;
    setOrdersMap(Object.fromEntries(ordersRef.current));
  };

  // Timer references for reconciliation
  const timersRef = useRef({});

  // WS setup
  const { isConnected, subscribeTopic } = useWebSocket({ page_id: 'manual-orders' });

  // Fetch initial REST state
  useEffect(() => {
    if (!userId) return;
    const fetchState = async () => {
      try {
        const res = await api.get('/orders/', { params: { strategy_instance_id: `manual:${userId}` } });
        const _apiOrders = res?.data?.data?.orders || res?.data?.data?.results;
        if (_apiOrders) {
          _apiOrders.forEach(order => {
            if (order.event_type && !order.status) {
              order.status = mapEventTypeToStatus(order.event_type);
            }
            //--> REST API returns order_id === null; fall back to id (same pattern as applyOrderDelta).
            const orderId = order.order_id || order.id;
            if (!orderId) return;
            ordersRef.current.set(orderId, { ...order, order_id: orderId });
          });
          forceRender();
        } else if (res?.data && Array.isArray(res.data)) {
          res.data.forEach(order => {
            if (order.event_type && !order.status) {
              order.status = mapEventTypeToStatus(order.event_type);
            }
            //--> REST API returns order_id === null; fall back to id (same pattern as applyOrderDelta).
            const orderId = order.order_id || order.id;
            if (!orderId) return;
            ordersRef.current.set(orderId, { ...order, order_id: orderId });
          });
          forceRender();
        }
      } catch (err) {
        console.error("[DIAG] ManualOrdersPanel REST fetch error", err);
      }
    };
    fetchState();
    //! refreshSignal is bumped by SmartOrderForm after every placement response, so a
    //!  just-placed order (including a REJECTED one, which is never armed) re-fetches and
    //!  appears in the panel immediately instead of only as a transient toast (#1).
  }, [userId, refreshSignal]);

  // Handle armedOrders expectations
  useEffect(() => {
    armedOrders.forEach(({ order_id, sentinel_sii }) => {
      if (!timersRef.current[order_id]) {
        console.log(`[DIAG] Arming order ${order_id}`);
        // Default to submitting state locally until WS confirms
        const existing = ordersRef.current.get(order_id);
        if (!existing) {
          ordersRef.current.set(order_id, {
            order_id,
            strategy_instance_id: sentinel_sii || `manual:${userId}`,
            status: 'PENDING',
            source: 'MANUAL',
            _optimistic: true
          });
          forceRender();
        }

        timersRef.current[order_id] = {
          submitTimer: setTimeout(() => reconcileOrder(order_id), SUBMITTED_GRACE_MS),
          terminalTimer: setTimeout(() => reconcileOrder(order_id), TERMINAL_GRACE_MS)
        };
      }
    });
  }, [armedOrders, userId]);

  const reconcileOrder = async (orderId) => {
    try {
      console.log(`[DIAG] Reconciling order ${orderId}`);
      // Fallback to explicit REST poll if WS hasn't advanced it
      const res = await api.get(`/orders/${orderId}`);
      const order = res?.data?.data ? res.data.data : res?.data;
      if (order) applyOrderDelta(order);
    } catch (err) {
      console.error(`[DIAG] Reconcile failed for ${orderId}`, err);
    }
  };

  const applyOrderDelta = (order) => {
    const orderId = order.order_id || order.id;
    if (!orderId) return;

    //--> Prefer the payload's explicit `status` (server truth — LV-M4, 2026-07-06);
    //    event_type mapping only as fallback (unmapped events must not become UNKNOWN).
    if (!order.status && order.event_type) {
      order.status = mapEventTypeToStatus(order.event_type);
    }
    //--> LV-M5 (2026-07-06): WS deltas carry `limit_price`, REST rows carry `price` —
    //    normalize so the additive merge can't pin a stale `price` on screen.
    if (order.price == null && (order.limit_price != null || order.trigger_price != null)) {
      order.price = order.limit_price ?? order.trigger_price;
    }

    const existing = ordersRef.current.get(orderId);
    const merged = { ...existing, ...order };

    if (!Object.prototype.hasOwnProperty.call(order, 'reconcile_state') && existing?.reconcile_state != null) {
      merged.reconcile_state = existing.reconcile_state;
    }

    if (existing?.status && TERMINAL_STATUSES.has(existing.status) && !TERMINAL_STATUSES.has(order.status)) {
      merged.status = existing.status;
      if (existing.filled_price != null) merged.filled_price = existing.filled_price;
      if (existing.filled_qty != null) merged.filled_qty = existing.filled_qty;
      if (existing.bar_start != null) merged.bar_start = existing.bar_start;
      if (existing.exit_reason != null) merged.exit_reason = existing.exit_reason;
      if (existing.slippage != null) merged.slippage = existing.slippage;
      if (existing.updated_at != null) merged.updated_at = existing.updated_at;
    }

    ordersRef.current.set(orderId, merged);
    forceRender();

    // Clear timers if we've reached states
    if (timersRef.current[orderId]) {
      const timers = timersRef.current[orderId];
      if (merged.status !== 'PENDING' && timers.submitTimer) {
        clearTimeout(timers.submitTimer);
        timers.submitTimer = null;
      }
      if (TERMINAL_STATUSES.has(merged.status) && timers.terminalTimer) {
        clearTimeout(timers.terminalTimer);
        timers.terminalTimer = null;
      }
    }
  };

  // WS Subscription
  useEffect(() => {
    if (!isConnected || !userId) return;
    
    const unsub = subscribeTopic(`user.${userId}.orders`, (msg) => {
      const order = msg?.data || msg;
      if (!String(order.strategy_instance_id || '').startsWith('manual:')) return;
      applyOrderDelta(order);
    });

    return () => {
      if (unsub) unsub();
    };
  }, [isConnected, userId, subscribeTopic]);

  // Fallback REST polling if disconnected
  // --> Max cap: 60 consecutive polls (5 min at 5s interval) then stop
  const disconnectedPollCountRef = useRef(0);
  const DISCONNECTED_POLL_MAX = 60;

  useEffect(() => {
    if (isConnected || !userId) {
      // Reset counter when connection is restored
      disconnectedPollCountRef.current = 0;
      return;
    }

    let intervalId = null;

    const poll = async () => {
      disconnectedPollCountRef.current += 1;
      if (disconnectedPollCountRef.current > DISCONNECTED_POLL_MAX) {
        console.log('[DIAG] ManualOrdersPanel disconnected poll capped after', DISCONNECTED_POLL_MAX, 'attempts — stopping fallback polling');
        clearInterval(intervalId);
        return;
      }

      try {
        const res = await api.get('/orders/', { params: { strategy_instance_id: `manual:${userId}` } });
        const _apiOrders = res?.data?.data?.orders || res?.data?.data?.results;
        if (_apiOrders) {
          _apiOrders.forEach(order => {
            if (order.event_type && !order.status) {
              order.status = mapEventTypeToStatus(order.event_type);
            }
            //--> REST API returns order_id === null; fall back to id (same pattern as applyOrderDelta).
            const orderId = order.order_id || order.id;
            if (!orderId) return;
            ordersRef.current.set(orderId, { ...order, order_id: orderId });
          });
          forceRender();
        } else if (res?.data && Array.isArray(res.data)) {
          res.data.forEach(order => {
            if (order.event_type && !order.status) {
              order.status = mapEventTypeToStatus(order.event_type);
            }
            //--> REST API returns order_id === null; fall back to id (same pattern as applyOrderDelta).
            const orderId = order.order_id || order.id;
            if (!orderId) return;
            ordersRef.current.set(orderId, { ...order, order_id: orderId });
          });
          forceRender();
        }
      } catch (err) {
        console.error("[DIAG] ManualOrdersPanel REST poll error", err);
      }
    };

    intervalId = setInterval(poll, 5000);
    return () => clearInterval(intervalId);
  }, [isConnected, userId]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      Object.values(timersRef.current).forEach(timers => {
        if (timers.submitTimer) clearTimeout(timers.submitTimer);
        if (timers.terminalTimer) clearTimeout(timers.terminalTimer);
      });
    };
  }, []);

  //--> Newest-first. REST orders carry `created_at`; WS deltas carry `timestamp` —
  //    fall back across both (+ updated_at) so REST-loaded rows don't all collapse to time 0.
  const _orderTime = (o) => {
    const raw = o.timestamp || o.created_at || o.updated_at || o.exchange_time;
    const t = raw ? new Date(raw).getTime() : 0;
    return Number.isNaN(t) ? 0 : t;
  };
  const ordersList = Object.values(ordersMap).sort((a, b) => _orderTime(b) - _orderTime(a));

  return (
    <div className="mt-4 border-t border-[var(--divider)] pt-2 flex flex-col gap-2">
      <h3 className="text-xs font-bold text-fg-2 uppercase">Manual Orders & Positions</h3>
      
      {!isConnected && (
        <div className="text-[10px] text-[var(--badge-loss-fg)] font-bold bg-[var(--badge-loss-bg)] p-1 rounded text-center">
          WS Disconnected - Polling fallback active
        </div>
      )}

      <div className="max-h-40 overflow-y-auto custom-scrollbar">
        {ordersList.length === 0 ? (
          <div className="text-xs text-fg-3 text-center py-2">No manual orders</div>
        ) : (
          <div className="flex flex-col gap-1">
            {ordersList.map(o => (
              <div key={o.order_id || o.id} className="flex justify-between items-center bg-bg-3 p-2 rounded text-xs">
                <div className="flex flex-col">
                  {/*--> order-instrument-identity-normalization P2b — display_symbol (stored canonical
                      name) first, then the #4b read-lane symbol resolve, then underlying_symbol/name;
                      NEVER instrument_id — post-2c that is a bare UUID and must not reach the screen. */}
                  <span className="font-bold text-fg-1">
                    {o.display_symbol ?? o.symbol ?? o.underlying_symbol ?? o.name ?? '—'}
                  </span>
                  <span className="text-[10px] text-fg-2">{o.side} {o.qty} @ {o.limit_price || 'MKT'}</span>
                  {/*--> Reject reason label: only shown when REJECTED and reason is present */}
                  {o.status === 'REJECTED' && rejectReasonLabel(o.reject_reason) && (
                    <span className="text-[10px] text-[var(--badge-loss-fg)]">
                      {rejectReasonLabel(o.reject_reason)}
                    </span>
                  )}
                </div>
                <div className={`px-2 py-1 rounded font-bold text-[10px] ${
                  o.status === 'FILLED' ? 'bg-[var(--badge-profit-bg)] text-[var(--badge-profit-fg)]' :
                  o.status === 'REJECTED' || o.status === 'CANCELLED' ? 'bg-[var(--badge-loss-bg)] text-[var(--badge-loss-fg)]' :
                  'bg-[var(--badge-info-bg)] text-[var(--badge-info-fg)]'
                }`}>
                  {o.status || 'PENDING'}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ManualOrdersPanel;
